<template>
  <BaseSearch
    ref="baseSearch"
    v-model="searchQuery"
    :loading="isLoading"
    :placeholder="$t(placeholder)"
    :close-on-outside-click="true"
    :clear-on-close="true"
    :open-title="$t('Search Location')"
    :close-title="$t('Close Search')"
    @search="performSearch"
    @open="$emit('search-opened')"
    @close="handleClose"
    @navigate="handleNavigate"
    input-width="200px"
    :isOutlined="!$q.screen.lt.md"
  >
    <template #results>
      <!-- Search Results Dropdown -->
      <Transition name="results-fade">
        <div v-if="searchResults.length > 0" class="search-results">
          <div
            v-for="(result, index) in searchResults" 
            :key="result.place_id || index"
            @click="selectSearchResult(result)"
            @mouseenter="highlightedIndex = index"
            class="search-result-item"
            :class="{ 'result-highlighted': highlightedIndex === index }"
          >
            <div class="result-content">
              <div class="result-name">{{ getResultName(result) }}</div>
              <div class="result-address">{{ getResultAddress(result) }}</div>
            </div>
          </div>   
        </div>
      </Transition>
    </template>
  </BaseSearch>
</template>

<script>
import { fromLonLat } from 'ol/proj'
import BaseSearch from '../ui/search.vue'

export default {
  name: 'MapSearch',
  
  components: { 
    BaseSearch 
  },
  
  props: {
    map: {
      type: Object,
      default: null
    },
    projection: {
      type: Object,
      required: true
    },
    placeholder: {
      type: String,
      default: "Search location..."
    },
    maxResults: {
      type: Number,
      default: 5
    },
    searchDelay: {
      type: Number,
      default: 300
    }
  },

  emits: [
    'location-selected',
    'search-opened',
    'search-closed',
    'search-started',
    'search-completed',
    'search-error'
  ],

  data() {
    return {
      searchQuery: '',
      searchResults: [],
      searchTimeout: null,
      isLoading: false,
      highlightedIndex: -1
    }
  },

  watch: {
    searchQuery(newQuery) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      
      if (newQuery.length > 2) {
        this.searchTimeout = setTimeout(() => {
          this.performSearch()
        }, this.searchDelay)
      } else {
        this.searchResults = []
      }
    }
  },

  beforeUnmount() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  },

  methods: {
    handleClose() {
      this.searchResults = []
      this.highlightedIndex = -1
      this.$emit('search-closed')
    },

    handleNavigate(direction) {
      if (this.searchResults.length === 0) return
      
      if (direction === 'down') {
        this.highlightedIndex = Math.min(
          this.highlightedIndex + 1, 
          this.searchResults.length - 1
        )
      } else if (direction === 'up') {
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0)
      }
    },

    async performSearch() {
      if (!this.searchQuery.trim()) return
      
      try {
        this.isLoading = true
        this.$emit('search-started', { query: this.searchQuery })
        
        console.log('🔍 Searching for:', this.searchQuery)
        
        const response = await this.get("DataCube/Search", { 
          q: this.searchQuery,
          limit: this.maxResults
        })

        if (!response) {
          throw new Error('Search request failed.')
        }
        
        this.searchResults = response.slice(0, this.maxResults)
        this.highlightedIndex = this.searchResults.length > 0 ? 0 : -1
        
        console.log('📍 Search results:', this.searchResults.length)
        this.$emit('search-completed', { 
          query: this.searchQuery, 
          results: this.searchResults 
        })
        
      } catch (error) {
        console.error('❌ Search error:', error)
        this.searchResults = []
        this.$emit('search-error', { 
          query: this.searchQuery, 
          error: error.message 
        })
      } finally {
        this.isLoading = false
      }
    },

    selectSearchResult(result) {
      console.log('📍 Selected location:', result)
      
      const lon = parseFloat(result.lon)
      const lat = parseFloat(result.lat)
      
      if (this.map && this.projection) {
        const view = this.map.getView()
        const center = fromLonLat([lon, lat], this.projection)
        const zoomLevel = this.calculateSmartZoom(result)
        
        console.log(`🎯 Animating to zoom ${zoomLevel}`)
        
        view.animate({
          center: center,
          zoom: zoomLevel,
          duration: 1000
        })
          
      }
      
      this.$emit('location-selected', {
        result,
        coordinates: [lon, lat],
        zoom: this.calculateSmartZoom(result)
      })
      
      // Clear results after selection
      this.searchResults = []
      this.searchQuery = ''
      this.$refs.baseSearch.close()
    },

    // Smart Zoom Logic
    calculateSmartZoom(result) {
      const { class: locationClass, type: locationType, importance, boundingbox } = result
      
      // Priority 1: Dynamic bounding box calculation
      if (boundingbox && boundingbox.length === 4) {
        const zoom = this.calculateZoomFromBoundingBox(boundingbox)
        if (zoom !== null) return zoom
      }
      
      // Priority 2: Type-based rules
      if (locationClass && locationType) {
        const typeZoom = this.getZoomByType(locationClass, locationType)
        if (typeZoom !== null) return typeZoom
      }
      
      // Priority 3: Class-only rules
      if (locationClass) {
        const classZoom = this.getZoomByClass(locationClass)
        if (classZoom !== null) return classZoom
      }
      
      // Priority 4: Importance score
      if (importance !== undefined && importance > 0) {
        return this.getZoomByImportance(importance)
      }
      
      return 16 // Fallback
    },

    calculateZoomFromBoundingBox(boundingbox) {
      try {
        const [south, north, west, east] = boundingbox.map(parseFloat)
        const latSpan = Math.abs(north - south)
        const lonSpan = Math.abs(east - west)
        const maxSpan = Math.max(latSpan, lonSpan)
        
        return this.calculateDynamicZoom(maxSpan)
      } catch (error) {
        return null
      }
    },

    calculateDynamicZoom(maxSpan) {
      const referenceSpan = 0.1
      const baseZoom = 12
      
      if (maxSpan <= 0) return 18
      
      const calculatedZoom = baseZoom - Math.log2(maxSpan / referenceSpan)
      return Math.max(3, Math.min(18, Math.round(calculatedZoom)))
    },

    getZoomByType(locationClass, locationType) {
      const zoomRules = {
        'place': {
          'country': 5, 'state': 6, 'region': 7, 'city': 10, 'town': 12,
          'village': 14, 'neighbourhood': 16
        },
        'highway': {
          'motorway': 12, 'primary': 14, 'secondary': 15, 'residential': 17
        },
        'amenity': 17,
        'building': 18,
        'tourism': 16,
        'historic': 16,
        'natural': 12
      }
      
      if (zoomRules[locationClass]) {
        const classRules = zoomRules[locationClass]
        if (typeof classRules === 'object') {
          return classRules[locationType] || null
        }
        return classRules
      }
      return null
    },

    getZoomByClass(locationClass) {
      const classZooms = {
        'amenity': 17, 'shop': 17, 'building': 18, 'tourism': 16,
        'historic': 16, 'natural': 12, 'leisure': 15, 'place': 12
      }
      return classZooms[locationClass] || null
    },

    getZoomByImportance(importance) {
      if (importance > 0.8) return 8
      if (importance > 0.6) return 10
      if (importance > 0.4) return 12
      if (importance > 0.2) return 14
      if (importance > 0.1) return 16
      return 17
    },

    // UI Helper Methods
    getResultName(result) {
      return result.display_name.split(',')[0] || result.name || 'Unknown'
    },

    getResultAddress(result) {
      const parts = result.display_name.split(',')
      return parts.slice(1).join(',').trim() || result.display_name
    }
  }
}
</script>

<style scoped>
/* Search Results */
.search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-top: none;
  border-radius: 0 0 8px 8px;
  max-height: 300px;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 101;
  margin-top: 0;
}

.search-result-item {
  padding: 12px;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 12px;
}

.search-result-item:hover,
.search-result-item.result-highlighted {
  background: #f9fafb;
}

.search-result-item:last-child {
  border-bottom: none;
}

.result-content {
  flex: 1;
  min-width: 0;
}

.result-name {
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-address {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Transitions */
.results-fade-enter-active,
.results-fade-leave-active {
  transition: all 0.2s ease;
}

.results-fade-enter-from,
.results-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>

<style>
/* Dark mode support */
body.body--dark .search-results {
  background: #1e1e1e;
  border-color: rgba(255, 255, 255, 0.1);
}

body.body--dark .search-result-item {
  border-bottom-color: rgba(255, 255, 255, 0.05);
}

body.body--dark .search-result-item:hover,
body.body--dark .search-result-item.result-highlighted {
  background: #2d2d2d;
}

body.body--dark .result-name {
  color: white;
}

body.body--dark .result-address {
  color: rgba(255, 255, 255, 0.6);
}
</style>